/**
 * Phase 101fs (Feb 2026) — Page « Réconciliation » (dashboard unifié).
 *
 * Alain (production, épuisé) : « je me retrouve à faire des BL, des ordres
 * après coup, des factures incomplètes… l'impression d'être un chirurgien
 * ayant oublié le bistouri ». Cet écran centralise TOUTES les anomalies
 * opérationnelles courantes en un seul endroit avec des liens directs
 * vers chaque entité (BL, CMD, Facture, Lot) pour les corriger en 1 clic.
 *
 * 6 catégories affichées :
 * - BL sans commande (client commercial) → lien vers /bons-livraison/:id
 * - CMD sans BL (statut avancé) → lien vers /commandes-clients/:id
 * - BL sans revendeur → lien BL (bouton "Attacher revendeur" y existe)
 * - Facture sans réf BL → lien facture
 * - Lots dupliqués → lien produit
 * - Lots orphelins (CMD annulée) → lien vers /stocks
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle, CheckCircle, RefreshCw, FileText, ClipboardList, Briefcase,
  FileSignature, Package, Boxes, ExternalLink, Loader2, PackageMinus, X, Truck,
} from "lucide-react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-CH", {
      day: "2-digit", month: "2-digit", year: "numeric",
    });
  } catch { return iso; }
};

const fmtChf = (n) => {
  if (n === null || n === undefined) return "—";
  return `${Number(n).toFixed(2)} CHF`;
};

// Configuration d'affichage par catégorie
const CATEGORY_CONFIG = {
  bl_without_order: {
    title: "BL sans commande",
    subtitle: "Bons de livraison créés directement, sans CMD attachée",
    icon: FileText,
    tone: "bl",
    columns: ["Référence", "Client", "Date livraison", "Statut", "Revendeur ?", "Actions"],
    renderRow: (row) => ({
      link: `/bons-livraison/${row.id}`,
      cells: [
        row.reference || "—",
        row.distributor_name || "—",
        fmtDate(row.delivery_date),
        <Badge variant="outline" className="text-xs">{row.status}</Badge>,
        row.has_reseller
          ? <Badge className="bg-[#F1F6EC] border border-[#88A066] text-[#3F6A2B]">oui</Badge>
          : <Badge className="bg-[#FFF4F1] border border-[#E54D2E] text-[#B03B1F]">non</Badge>,
      ],
    }),
  },
  order_without_bl: {
    title: "Commandes sans BL",
    subtitle: "CMD en cours (VALIDEE / EN_PRODUCTION / PRETE) sans bon de livraison généré",
    icon: ClipboardList,
    tone: "co",
    columns: ["Référence", "Client", "Livraison prévue", "Statut", "Lignes", "Actions"],
    hasQuickGenerateBL: true,   // Phase 101ft — bouton "Générer BL" 1-clic
    renderRow: (row) => ({
      link: `/commandes-clients/${row.id}`,
      cells: [
        row.reference || "—",
        row.distributor_name || "—",
        fmtDate(row.delivery_date_target),
        <Badge variant="outline" className="text-xs">{row.status}</Badge>,
        <span className="font-mono text-xs">{row.lines_count}</span>,
      ],
      generateBLContext: { orderId: row.id, orderRef: row.reference || row.id },
    }),
  },
  bl_without_reseller: {
    title: "BL sans revendeur",
    subtitle: "BL vers un client commercial mais sans revendeur B2B lié — prix négociés non appliqués",
    icon: Briefcase,
    tone: "reseller",
    columns: ["Référence", "Client", "Date livraison", "Statut", "Lignes", "Actions"],
    renderRow: (row) => ({
      link: `/bons-livraison/${row.id}`,
      cells: [
        row.reference || "—",
        row.distributor_name || "—",
        fmtDate(row.delivery_date),
        <Badge variant="outline" className="text-xs">{row.status}</Badge>,
        <span className="font-mono text-xs">{row.lines_count}</span>,
      ],
      actionLabel: "Attacher revendeur →",
    }),
  },
  invoice_missing_bl_ref: {
    title: "Factures sans référence BL",
    subtitle: "Factures liées à une CMD avec BL mais le numéro BL n'a pas été reporté sur la facture",
    icon: FileSignature,
    tone: "inv",
    columns: ["Référence", "Débiteur", "Date", "Statut", "TTC", "Actions"],
    hasQuickFix: true,       // Phase 101fs — backfill 1-clic
    renderRow: (row) => ({
      link: `/factures/${row.id}`,
      cells: [
        row.reference || "—",
        row.debtor_name || "—",
        fmtDate(row.issue_date),
        <Badge variant="outline" className="text-xs">{row.status}</Badge>,
        fmtChf(row.total_ttc),
      ],
    }),
  },
  duplicate_lots: {
    title: "Lots dupliqués",
    subtitle: "Même numéro de lot avec plusieurs entrées actives — candidats pour sortie audit",
    icon: Boxes,
    tone: "lot",
    columns: ["Produit", "SKU", "N° lot", "Occurrences", "Qté totale", "Actions"],
    hasAuditOut: true,   // Phase 101fs — sortie audit sur chaque lot
    renderRow: (row) => ({
      link: `/stocks?q=${encodeURIComponent(row.lot_number || "")}`,
      cells: [
        row.product_name || "—",
        <span className="font-mono text-xs">{row.product_sku || "—"}</span>,
        <span className="font-mono text-xs">{row.lot_number || "—"}</span>,
        <Badge className="bg-[#FBF4E9] border border-[#E8D3A8] text-[#8C6B2F]">{row.duplicate_count} lots</Badge>,
        <span className="font-mono text-xs">{row.total_quantity}</span>,
      ],
      auditContext: {
        lotIds: row.lot_ids || [],
        productName: row.product_name || "?",
        lotNumber: row.lot_number || "",
        defaultReason: "duplicate",
      },
    }),
  },
  overproduction_lots: {
    title: "Lots orphelins (CMD annulée)",
    subtitle: "Lots produits pour une CMD qui a été annulée — encore en stock",
    icon: Package,
    tone: "over",
    columns: ["Produit", "N° lot", "Qté", "DLC", "CMD annulée", "Actions"],
    hasAuditOut: true,
    renderRow: (row) => ({
      link: `/stocks?q=${encodeURIComponent(row.lot_number || "")}`,
      cells: [
        row.product_name || "—",
        <span className="font-mono text-xs">{row.lot_number || "—"}</span>,
        <span className="font-mono text-xs">{row.quantity}</span>,
        fmtDate(row.dlc),
        <span className="text-xs">
          {row.cancelled_order_reference || "—"}
          {row.cancelled_order_client ? ` · ${row.cancelled_order_client}` : ""}
        </span>,
      ],
      auditContext: {
        lotIds: [row.lot_id],
        productName: row.product_name || "?",
        lotNumber: row.lot_number || "",
        defaultReason: "overproduction",
      },
    }),
  },
};

export default function Reconciliation() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  // Phase 101ft — filtres globaux
  const [filters, setFilters] = useState({ client_id: "", date_from: "", date_to: "" });

  const load = async (nextFilters) => {
    const eff = nextFilters || filters;
    setLoading(true);
    try {
      const params = {};
      if (eff.client_id) params.client_id = eff.client_id;
      if (eff.date_from) params.date_from = eff.date_from;
      if (eff.date_to) params.date_to = eff.date_to;
      const { data } = await api.get("/reconciliation/summary", { params });
      setData(data);
      if (data.total_anomalies === 0) {
        toast.success("✅ Aucune anomalie détectée sur ce périmètre !");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const hasActiveFilters = Boolean(filters.client_id || filters.date_from || filters.date_to);
  const availableClients = data?.available_clients || [];

  const applyFilters = () => load(filters);
  const clearFilters = () => {
    const cleared = { client_id: "", date_from: "", date_to: "" };
    setFilters(cleared);
    load(cleared);
  };

  const categories = data?.categories || {};
  const totalAnomalies = data?.total_anomalies || 0;

  return (
    <div className="p-6 space-y-6" data-testid="reconciliation-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-medium text-[#1A1C1A]">Réconciliation</h1>
          <p className="text-sm text-[#575A57] mt-1 max-w-2xl">
            Toutes les anomalies opérationnelles regroupées ici. Clique sur une ligne pour
            ouvrir l'entité et la corriger.
          </p>
        </div>
        <Button
          onClick={() => load()}
          disabled={loading}
          className="bg-[#1F4E3D] hover:bg-[#173B2E] text-white"
          data-testid="reconciliation-refresh-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          {loading ? "Analyse…" : "Actualiser"}
        </Button>
      </div>

      {/* Phase 101ft — Barre de filtres globaux */}
      <Card className="border-[#E5E5E0]" data-testid="reconciliation-filters">
        <CardContent className="p-4">
          <div className="flex items-end gap-3 flex-wrap">
            <div className="flex-1 min-w-[220px]">
              <Label className="text-xs uppercase tracking-wide text-[#575A57] mb-1 block">Client</Label>
              <Select
                value={filters.client_id || "__all__"}
                onValueChange={(v) => setFilters((f) => ({ ...f, client_id: v === "__all__" ? "" : v }))}
              >
                <SelectTrigger data-testid="reconciliation-filter-client">
                  <SelectValue placeholder="Tous les clients" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Tous les clients</SelectItem>
                  {availableClients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-[#575A57] mb-1 block">Date début</Label>
              <Input
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
                data-testid="reconciliation-filter-date-from"
                className="w-[160px]"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-[#575A57] mb-1 block">Date fin</Label>
              <Input
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
                data-testid="reconciliation-filter-date-to"
                className="w-[160px]"
              />
            </div>
            <Button
              onClick={applyFilters}
              disabled={loading}
              className="bg-[#1F4E3D] hover:bg-[#173B2E] text-white"
              data-testid="reconciliation-apply-filters"
            >
              Filtrer
            </Button>
            {hasActiveFilters && (
              <Button
                variant="outline"
                onClick={clearFilters}
                disabled={loading}
                data-testid="reconciliation-clear-filters"
              >
                <X className="w-3 h-3 mr-1" />
                Réinitialiser
              </Button>
            )}
          </div>
          {hasActiveFilters && (
            <div className="mt-3 text-xs text-[#8C6B2F]">
              🔎 Vue filtrée — {filters.client_id ? `client sélectionné` : ""}
              {filters.client_id && (filters.date_from || filters.date_to) ? " · " : ""}
              {filters.date_from && `du ${filters.date_from}`}
              {filters.date_from && filters.date_to && " "}
              {filters.date_to && `au ${filters.date_to}`}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bandeau résumé global */}
      {data && (
        <Card
          className={totalAnomalies === 0
            ? "border-[#88A066] bg-[#F1F6EC]"
            : "border-[#E54D2E] bg-[#FFF4F1]"}
          data-testid="reconciliation-header-card"
        >
          <CardContent className="p-4 flex items-center gap-3">
            {totalAnomalies === 0 ? (
              <>
                <CheckCircle className="w-6 h-6 text-[#88A066]" />
                <div>
                  <div className="font-medium text-[#3F6A2B]">Tout est propre</div>
                  <div className="text-xs text-[#575A57]">Aucune anomalie détectée sur les 6 catégories surveillées.</div>
                </div>
              </>
            ) : (
              <>
                <AlertTriangle className="w-6 h-6 text-[#E54D2E]" />
                <div>
                  <div className="font-medium text-[#B03B1F]">
                    {totalAnomalies} anomalie{totalAnomalies > 1 ? "s" : ""} détectée{totalAnomalies > 1 ? "s" : ""}
                  </div>
                  <div className="text-xs text-[#575A57]">Détail par catégorie ci-dessous.</div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Cartes par catégorie */}
      {data && Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => {
        const cat = categories[key] || { count: 0, sample: [] };
        return (
          <CategoryCard
            key={key}
            catKey={key}
            config={cfg}
            count={cat.count}
            sample={cat.sample}
            onReload={load}
          />
        );
      })}

      {!data && loading && (
        <div className="text-center py-12 text-[#575A57]">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
          Chargement…
        </div>
      )}
    </div>
  );
}

function CategoryCard({ catKey, config, count, sample, onReload }) {
  const Icon = config.icon;
  const isEmpty = count === 0;
  const [bulkLoading, setBulkLoading] = useState(false);
  const [auditTarget, setAuditTarget] = useState(null); // { lotIds, productName, lotNumber, defaultReason }

  const runBulkBackfill = async () => {
    if (!config.hasQuickFix || sample.length === 0) return;
    setBulkLoading(true);
    let success = 0, failed = 0, upToDate = 0;
    for (const row of sample) {
      try {
        const { data } = await api.post(`/invoices/${row.id}/backfill-bl-ref`);
        if (data.already_up_to_date) upToDate += 1;
        else success += 1;
      } catch (_) { failed += 1; }
    }
    setBulkLoading(false);
    if (success > 0) {
      toast.success(`✅ ${success} facture(s) backfillée(s)${upToDate ? `, ${upToDate} déjà à jour` : ""}${failed ? `, ${failed} échec(s)` : ""}`);
    } else if (upToDate > 0 && failed === 0) {
      toast.info(`Aucun changement — les ${upToDate} facture(s) affichée(s) sont déjà à jour.`);
    } else if (failed > 0) {
      toast.error(`${failed} échec(s) sur ${sample.length} tentative(s)`);
    }
    onReload?.();
  };

  return (
    <Card
      className={isEmpty ? "border-[#E5E5E0]" : "border-[#E8D3A8]"}
      data-testid={`reconciliation-category-${catKey}`}
    >
      <CardContent className="p-0">
        <div className="p-4 flex items-start justify-between gap-3 border-b border-[#E5E5E0]">
          <div className="flex items-start gap-3">
            <div className={`w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0 ${
              isEmpty ? "bg-[#F1F6EC] text-[#88A066]" : "bg-[#FBF4E9] text-[#8C6B2F]"
            }`}>
              <Icon className="w-5 h-5" />
            </div>
            <div>
              <div className="font-medium text-[#1A1C1A]">{config.title}</div>
              <div className="text-xs text-[#575A57] mt-0.5 max-w-xl">{config.subtitle}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {config.hasQuickFix && sample.length > 0 && !isEmpty && (
              <Button
                variant="outline"
                size="sm"
                className="border-[#8C6B2F] text-[#8C6B2F] hover:bg-[#FBF4E9]"
                onClick={runBulkBackfill}
                disabled={bulkLoading}
                data-testid={`reconciliation-bulk-fix-${catKey}`}
              >
                {bulkLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                Backfill affichés ({sample.length})
              </Button>
            )}
            <Badge
              className={isEmpty
                ? "bg-[#F1F6EC] border border-[#88A066] text-[#3F6A2B]"
                : "bg-[#FBF4E9] border border-[#E8D3A8] text-[#8C6B2F]"}
              data-testid={`reconciliation-count-${catKey}`}
            >
              {count}
            </Badge>
          </div>
        </div>

        {sample.length === 0 ? (
          <div className="p-6 text-center text-sm text-[#A3A6A3]">
            {isEmpty ? "Rien à réconcilier ici ✓" : `${count} élément(s) — chargement…`}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                {config.columns.map((c) => (
                  <TableHead key={c} className={c === "Actions" ? "text-right" : ""}>{c}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sample.map((row, idx) => {
                const r = config.renderRow(row);
                const key = row.id || row.lot_id || `${row.product_id}-${row.lot_number}-${idx}`;
                return (
                  <TableRow key={key} data-testid={`reconciliation-row-${catKey}-${idx}`}>
                    {r.cells.map((cell, i) => (
                      <TableCell key={i}>{cell}</TableCell>
                    ))}
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-3">
                        {config.hasAuditOut && r.auditContext && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-[#8C6B2F] text-[#8C6B2F] hover:bg-[#FBF4E9] h-7 px-2 text-xs"
                            onClick={() => setAuditTarget(r.auditContext)}
                            data-testid={`reconciliation-audit-out-${catKey}-${idx}`}
                          >
                            <PackageMinus className="w-3 h-3 mr-1" />
                            Sortie audit
                          </Button>
                        )}
                        {config.hasQuickGenerateBL && r.generateBLContext && (
                          <QuickGenerateBLButton
                            catKey={catKey}
                            idx={idx}
                            context={r.generateBLContext}
                            onDone={onReload}
                          />
                        )}
                        <Link
                          to={r.link}
                          className="inline-flex items-center gap-1 text-[#1F4E3D] hover:text-[#173B2E] text-sm font-medium"
                          data-testid={`reconciliation-open-${catKey}-${idx}`}
                        >
                          {r.actionLabel || "Ouvrir"}
                          <ExternalLink className="w-3 h-3" />
                        </Link>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        {count > sample.length && (
          <div className="p-3 text-center text-xs text-[#A3A6A3] border-t border-[#E5E5E0] bg-[#FAFAF7]">
            {count - sample.length} élément(s) supplémentaire(s) non affiché(s) — corrige les
            10 premiers puis actualise pour voir la suite.
          </div>
        )}
      </CardContent>

      {auditTarget && (
        <AuditOutDialog
          target={auditTarget}
          onClose={() => setAuditTarget(null)}
          onDone={() => { setAuditTarget(null); onReload?.(); }}
        />
      )}
    </Card>
  );
}

// ============================================================================
// Phase 101ft — Bouton 1-clic "Générer BL" depuis une CMD sans BL
// ============================================================================
function QuickGenerateBLButton({ catKey, idx, context, onDone }) {
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/client-orders/${context.orderId}/generate-bl`);
      const blId = data?.bl_id;
      if (blId) {
        toast.success(`✅ BL généré depuis ${context.orderRef}`, {
          action: {
            label: "Ouvrir",
            onClick: () => navigate(`/bons-livraison/${blId}`),
          },
        });
      } else {
        toast.success(`✅ BL généré depuis ${context.orderRef}`);
      }
      onDone?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Erreur";
      toast.error(`Échec génération BL : ${detail}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      variant="outline"
      size="sm"
      className="border-[#1F4E3D] text-[#1F4E3D] hover:bg-[#F1F6EC] h-7 px-2 text-xs"
      onClick={submit}
      disabled={busy}
      data-testid={`reconciliation-generate-bl-${catKey}-${idx}`}
    >
      {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Truck className="w-3 h-3 mr-1" />}
      {busy ? "Génération…" : "Générer BL"}
    </Button>
  );
}


// ============================================================================
// Phase 101fs — Dialog de sortie audit (doublon / surproduction)
// ============================================================================
function AuditOutDialog({ target, onClose, onDone }) {
  const [reason, setReason] = useState(target.defaultReason || "duplicate");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const lotIds = target.lotIds || [];
  const isBulk = lotIds.length > 1;

  const submit = async () => {
    if (!notes.trim()) {
      toast.error("Ajoute une justification (motif audit obligatoire pour la traçabilité HACCP)");
      return;
    }
    setBusy(true);
    let success = 0, failed = 0;
    for (const lotId of lotIds) {
      try {
        await api.post(`/lots/${lotId}/audit-out`, {
          audit_reason: reason,
          notes: notes.trim(),
          quantity: null,
        });
        success += 1;
      } catch (e) {
        failed += 1;
      }
    }
    setBusy(false);
    if (success > 0) {
      toast.success(`✅ ${success} lot(s) sorti(s) en audit${failed ? `, ${failed} échec(s)` : ""}`);
      onDone?.();
    } else {
      toast.error(`Aucune sortie effectuée (${failed} échec(s))`);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="reconciliation-audit-out-dialog">
        <DialogHeader>
          <DialogTitle>Sortie audit — {target.productName}</DialogTitle>
          <DialogDescription>
            Lot n° <span className="font-mono">{target.lotNumber}</span>
            {isBulk && ` · ${lotIds.length} lots concernés (toutes les entrées dupliquées)`}
            <br />
            Cette action retire <strong>la totalité des quantités restantes</strong> des lots sélectionnés
            avec un mouvement OUT traçable HACCP. Irréversible.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs uppercase tracking-wide text-[#575A57]">Motif</Label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              {[
                { v: "duplicate", label: "Doublon" },
                { v: "overproduction", label: "Surproduction" },
                { v: "other", label: "Autre" },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setReason(opt.v)}
                  className={`text-xs py-2 px-2 rounded border ${
                    reason === opt.v
                      ? "border-[#8C6B2F] bg-[#FBF4E9] text-[#8C6B2F] font-medium"
                      : "border-[#E5E5E0] bg-white text-[#575A57] hover:bg-[#FAFAF7]"
                  }`}
                  data-testid={`audit-reason-${opt.v}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label htmlFor="audit-notes" className="text-xs uppercase tracking-wide text-[#575A57]">
              Justification <span className="text-[#E54D2E]">*</span>
            </Label>
            <Textarea
              id="audit-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ex: doublon créé lors d'un import Nayax raté le 12/09/2026"
              rows={3}
              className="mt-1"
              data-testid="audit-notes-input"
            />
            <div className="text-xs text-[#A3A6A3] mt-1">
              Cette note sera enregistrée dans le mouvement HACCP.
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={busy}
            data-testid="audit-cancel-btn"
          >
            Annuler
          </Button>
          <Button
            onClick={submit}
            disabled={busy || !notes.trim()}
            className="bg-[#8C6B2F] hover:bg-[#6E5324] text-white"
            data-testid="audit-confirm-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PackageMinus className="w-4 h-4 mr-2" />}
            {busy ? "Sortie…" : `Sortir ${lotIds.length} lot${lotIds.length > 1 ? "s" : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
